import { lazy } from 'react';
import {
  FileUp,
  Shield,
  FileText,
  ClipboardList,
  MessageSquare,
  Library,
  Brain,
  Workflow,
  FlaskConical,
  Sparkles,
  MonitorUp,
} from 'lucide-react';
import { createRoute } from './utils';
import { AppRouteConfig, RouteDefinition } from '@/types/routes';

// Lazy load configuration section components
const DocumentUploaderPage = lazy(() => import('@/pages/document-uploader/DocumentUploaderPage'));
// Document workflow (split-page) routes
const DocumentWorkflowIntroPage = lazy(() => import('@/pages/documents/DocumentWorkflowIntroPage'));
const DocumentProcessingPage = lazy(() => import('@/pages/documents/DocumentProcessingPage'));
const DocumentChatPage = lazy(() => import('@/pages/documents/DocumentChatPage'));
const DocumentProcessingSummaryPage = lazy(() => import('@/pages/documents/DocumentProcessingSummaryPage'));
const DocumentReviewPage = lazy(() => import('@/pages/documents/DocumentReviewPage'));
const DocumentProcessingCompletePage = lazy(() => import('@/pages/documents/DocumentProcessingCompletePage'));
const AgentBuilderPage = lazy(() => import('@/pages/agent-builder/AgentBuilderPage'));
const TeammateStudioPage = lazy(() => import('@/pages/teammate-studio/TeammateStudioPage'));
const TeammateCreateEditPage = lazy(() => import('@/pages/teammate-studio/TeammateCreateEditPage'));
const TeamOrgChartPage = lazy(() => import('@/pages/teammate-studio/TeamOrgChartPage'));
const OptimisationReviewPage = lazy(() => import('@/pages/teammate-studio/OptimisationReviewPage'));
const RationaleDiscussionPage = lazy(() => import('@/pages/teammate-studio/RationaleDiscussionPage'));
const AuditClientPage = lazy(() => import('@/pages/audit/AuditClientPage'));
const DeveloperLogsPage = lazy(() => import('@/pages/developer/DeveloperLogsPage'));
const PolicyCategoriesOverviewPage = lazy(() => import('@/pages/rules/PolicyCategoriesOverviewPage'));
const ChatPage = lazy(() => import('@/pages/chat/ChatPage'));
const KnowledgeLibraryPage = lazy(() => import('@/pages/knowledge-library/KnowledgeLibraryPage'));
const MemoryLibraryPage = lazy(() => import('@/pages/memory-library/MemoryLibraryPage'));
const SkillLibraryPage = lazy(() => import('@/pages/skill-library/SkillLibraryPage'));
const TriggersPage = lazy(() => import('@/pages/triggers/TriggersPage'));
const TriggerEditPage = lazy(() => import('@/pages/triggers/TriggerEditPage'));
const EvalStudioPage = lazy(() => import('@/pages/eval-studio/EvalStudioPage'));
const ScenarioEditPage = lazy(() => import('@/pages/eval-studio/ScenarioEditPage'));
const ComputerTakeoverPage = lazy(() => import('@/pages/computers/ComputerTakeoverPage'));

// Configuration section routes
export const configurationSection: AppRouteConfig = {
  sectionTitle: 'Configuration',
  routes: [
    createRoute('documents', <DocumentUploaderPage />, 'Document Upload', FileUp) as RouteDefinition,
    // Hidden workflow routes
    createRoute('documents/:documentId/intro', <DocumentWorkflowIntroPage />, 'Document Intro', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('documents/:documentId/chat', <DocumentChatPage />, 'Document Chat', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('documents/:documentId/processing', <DocumentProcessingPage />, 'Document Processing', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('documents/:documentId/summary', <DocumentProcessingSummaryPage />, 'Processing Summary', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('documents/:documentId/review', <DocumentReviewPage />, 'Document Review', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('documents/:documentId/complete', <DocumentProcessingCompletePage />, 'Document Complete', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('rules', <PolicyCategoriesOverviewPage />, 'Policy Rules', Shield) as RouteDefinition,
    createRoute('teammate-studio', <TeammateStudioPage />, 'Teammate Studio', FileText) as RouteDefinition,
    createRoute('teammate-studio/new', <TeammateCreateEditPage />, 'New Teammate', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('teammate-studio/:agentId/edit', <TeammateCreateEditPage />, 'Edit Teammate', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute(
      'teammate-studio/:agentId/optimisation-reviews/:reviewId',
      <OptimisationReviewPage />,
      'Optimisation Review',
      undefined,
      { hidden: true }
    ) as RouteDefinition,
    createRoute(
      'teammate-studio/:agentId/versions/:versionId/rationale-chat',
      <RationaleDiscussionPage />,
      'Rationale discussion',
      undefined,
      { hidden: true }
    ) as RouteDefinition,
    createRoute('teammate-studio/legacy', <AgentBuilderPage />, 'Teammate Studio (Legacy)', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('teammate-studio/org-chart', <TeamOrgChartPage />, 'Team Org Chart', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('knowledge-library', <KnowledgeLibraryPage />, 'Knowledge Library', Library) as RouteDefinition,
    createRoute('memory-library', <MemoryLibraryPage />, 'Memory Library', Brain) as RouteDefinition,
    createRoute('skill-library', <SkillLibraryPage />, 'Skill Library', Sparkles) as RouteDefinition,
    createRoute('triggers', <TriggersPage />, 'Triggers', Workflow) as RouteDefinition,
    createRoute('triggers/:triggerId/edit', <TriggerEditPage />, 'Edit Trigger', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('eval-studio', <EvalStudioPage />, 'Eval Studio', FlaskConical) as RouteDefinition,
    createRoute('eval-studio/:scenarioId/edit', <ScenarioEditPage />, 'Edit Scenario', undefined, {
      hidden: true,
    }) as RouteDefinition,
    createRoute('chat', <ChatPage />, 'AI Assistant', MessageSquare) as RouteDefinition,
    createRoute('computer-control', <ComputerTakeoverPage />, 'Computer Control', MonitorUp) as RouteDefinition,
    createRoute('audit', <AuditClientPage />, 'Audit Logs', ClipboardList) as RouteDefinition,
    createRoute('developer-logs', <DeveloperLogsPage />, 'Developer Logs', FileText) as RouteDefinition,
  ],
};
